import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

function toInt(value) {
  return parseInt(value, 10);
}

export default class extends Controller {
  static targets = ["slider", "surface", "label", "value"];

  static values = {
    vehicleId: Number,
    showLabels: Boolean,
  };

  connect() {
    this.subscribe();

    this.sliderTargets.forEach((slider) => this.updateSlider(slider));
    this.labelOrder = this.labelTargets.map(({ dataset: { batterySegmentId } }) => batterySegmentId);
    this.hideOverlappingLabels();
    this.repositionLabels();

    if (!this.showLabelsValue) {
      this.labelTargets.forEach((label) => {
        return label.classList.add("sr-only");
      });
    }
  }

  subscribe() {
    this.unsubscribe();

    this.subscription = consumer.subscriptions.create(
      { channel: "VehicleUpdateChannel" },
      { received: this.received.bind(this) }
    );
  }

  disconnect() {
    this.unsubscribe();
  }

  unsubscribe() {
    if (this.subscription) this.subscription.unsubscribe();
  }

  received({ id, latest_battery_level_value: latestBatteryLevelValue }) {
    if (id !== this.vehicleIdValue) return;

    this.setSliderValue("actual-soc", latestBatteryLevelValue * 100);
    this.element.dispatchEvent(new Event("change", { bubbles: true }));
  }

  sliderChanged({ target }) {
    const {
      dataset: { batterySegmentId },
      value,
    } = target;
    this.updateSlider(target);
    this.pushLabelToFront(batterySegmentId);

    const sliderIndex = this.sliderTargets.findIndex(
      (surface) => surface.dataset.batterySegmentId === batterySegmentId
    );
    const sliderValue = toInt(value);

    this.sliderTargets.slice(sliderIndex + 1).forEach((neighbourSlider) => {
      const neighbourValue = toInt(neighbourSlider.value);

      if (neighbourValue < sliderValue && batterySegmentId !== "actual-soc") {
        this.setSliderValue(neighbourSlider.dataset.batterySegmentId, value);
      }
    });

    this.sliderTargets.slice(0, sliderIndex).forEach((neighbourSlider) => {
      const neighbourValue = toInt(neighbourSlider.value);

      if (
        !neighbourSlider.disabled &&
        neighbourValue > sliderValue &&
        neighbourSlider.dataset.batterySegmentId !== "actual-soc"
      ) {
        this.setSliderValue(neighbourSlider.dataset.batterySegmentId, value);
      }
    });

    if (this.showLabelsValue) {
      this.hideOverlappingLabels();
      this.repositionLabels();
    }
  }

  hideOverlappingLabels() {
    this.sliderTargets.slice(1).forEach((slider, index) => {
      const leftNeighbour = this.sliderTargets[index];
      const element = this.find(this.labelTargets, slider.dataset.batterySegmentId);

      if (element) {
        const neighbourIsActualSOC = leftNeighbour.dataset.batterySegmentId === "actual-soc";
        element.classList.toggle(
          "invisible",
          !neighbourIsActualSOC && toInt(leftNeighbour.value) >= toInt(slider.value)
        );
      }
    });
  }

  find(targetsList, batterySegmentId) {
    return targetsList.find((target) => target.dataset.batterySegmentId === batterySegmentId);
  }

  pushLabelToFront(batterySegmentId) {
    this.labelOrder = this.labelOrder.filter((item) => item !== batterySegmentId);
    this.labelOrder.push(batterySegmentId);
  }

  setSliderValue(batterySegmentId, value) {
    const roundedValue = Math.round(value);
    this.find(this.sliderTargets, batterySegmentId).value = roundedValue;
    this.updateSurface(batterySegmentId, roundedValue);
  }

  updateSlider({ dataset: { batterySegmentId }, value }) {
    this.updateSurface(batterySegmentId, value);
  }

  updateSurface(batterySegmentId, value) {
    this.surfaceTargets
      .filter((surface) => surface.dataset.batterySegmentId === batterySegmentId)
      .map((surface) => (surface.style.width = `${value}%`));

    const element = this.find(this.valueTargets, batterySegmentId);
    if (element) element.innerText = `${value}%`;
  }

  getLeftNeighbourValue({ dataset: { batterySegmentId }, value }) {
    const leftNeighbourValues = this.sliderTargets
      .filter((slider) => slider.dataset.batterySegmentId !== batterySegmentId)
      .map((slider) => toInt(slider.value))
      .filter((neighbourValue) => neighbourValue < value);

    return Math.max(...leftNeighbourValues);
  }

  repositionLabels() {
    const labelOrderMap = this.labelOrder.reduce(
      (acc, batterySegmentId, index) => ({ ...acc, [batterySegmentId]: index }),
      {}
    );

    this.labelTargets.forEach((label) => {
      label.style.zIndex = labelOrderMap[label.dataset.batterySegmentId] + 10;
    });

    for (let i = this.sliderTargets.length - 1; i >= 0; i -= 1) {
      const thisSlider = this.sliderTargets[i];
      const ownValue = toInt(thisSlider.value);
      const leftNeighbourValue = thisSlider.disabled ? 0 : this.getLeftNeighbourValue(thisSlider);
      const center = (ownValue - leftNeighbourValue) * 0.5 * (100 / ownValue);
      const correctionForPerspective = 2 * ((ownValue + leftNeighbourValue) / 2 - 50) * 3.274 * 0.01;
      const position = center + correctionForPerspective;
      const element = this.find(this.labelTargets, thisSlider.dataset.batterySegmentId);
      if (element) element.style.right = `${position}%`;
    }
  }
}
